import { createApp } from 'vue';
import { createPinia, setActivePinia } from 'pinia';
import App from './App.vue';
import router from './router';
// ✅ NEW: Import centralized project config
import eckpunktConfig from './config/project.config';
import eckpunktBranding from './config/branding.config';
import { PROJECT_CONFIG_KEY } from '@shared';
// Import restaurant configuration and API client (LEGACY - will be migrated)
import restaurantConfig from '../restaurant.config';
import { RestaurantApiClient, loadSiteConfig } from '@shared';
// Create API client from restaurant config
const projectConfig = {
    name: restaurantConfig.restaurant.name,
    apiBaseUrl: restaurantConfig.app.api.baseUrl,
    publicApiBaseUrl: restaurantConfig.app.api.publicUrl,
    projectIdentifier: restaurantConfig.restaurant.projectId,
    customHeaders: {
        'X-Project-Domain': restaurantConfig.app.domains.production,
        'X-Restaurant-ID': restaurantConfig.restaurant.projectId
    },
    theme: {
        primaryColor: restaurantConfig.app.theme.primaryColor,
        secondaryColor: restaurantConfig.app.theme.secondaryColor,
        accentColor: restaurantConfig.app.theme.accentColor
    },
    // ✅ NEW: Add branding configuration for extended theme support
    branding: eckpunktBranding
};
console.log('🔧 Main.ts ProjectConfig Debug:');
console.log('  - Restaurant Config API baseUrl:', restaurantConfig.app.api.baseUrl);
console.log('  - Restaurant Config API publicUrl:', restaurantConfig.app.api.publicUrl);
console.log('  - Final ProjectConfig apiBaseUrl:', projectConfig.apiBaseUrl);
console.log('  - Final ProjectConfig publicApiBaseUrl:', projectConfig.publicApiBaseUrl);
console.log('  - ProjectConfig object:', projectConfig);
console.log('  - ProjectConfig.branding:', projectConfig.branding);
console.log('  - ProjectConfig.branding.colors.primary:', projectConfig.branding?.colors?.primary);
const apiClient = new RestaurantApiClient(projectConfig);
// Create project context from restaurant config
const projectContext = {
    name: restaurantConfig.restaurant.shortName,
    description: restaurantConfig.restaurant.description,
    navigation: [
        { name: 'Home', path: '/', icon: 'home' },
        { name: 'Speisekarte', path: '/speisekarte', icon: 'utensils' },
        { name: 'Warenkorb', path: '/warenkorb', icon: 'shopping-cart' }
    ],
    ctaText: 'Jetzt bestellen',
    contactInfo: {
        address: `${restaurantConfig.restaurant.address.street} ${restaurantConfig.restaurant.address.number}, ${restaurantConfig.restaurant.address.postalCode} ${restaurantConfig.restaurant.address.city}`,
        phone: restaurantConfig.restaurant.contact.phone,
        email: restaurantConfig.restaurant.contact.email
    },
    theme: {
        primaryColor: restaurantConfig.app.theme.primaryColor,
        accentColor: restaurantConfig.app.theme.accentColor
    }
};
// ✅ FontAwesome CSS Import - Complete Icon Library
import "@fortawesome/fontawesome-free/css/all.min.css";
import '@shared/styles/main.css';
import './styles/main.css';
import './styles/badges.css';
console.log('🍽️ Eckpunkt Restaurant initializing...');
// ✅ ENVIRONMENT: Initialize environment detection
import { initializeEnvironment } from '@shared/utils/environment';
initializeEnvironment();
// Restaurant configuration loaded from restaurant.config.ts
console.log('🏭 Restaurant Config loaded:', restaurantConfig.restaurant.name);
console.log('🌐 API Base URL:', restaurantConfig.app.api.baseUrl);
// Create Vue app
const app = createApp(App);
// Global error handler
app.config.errorHandler = (err, _instance, info) => {
    console.error('Vue App Error:', err, info);
};
// Create and use Pinia store FIRST
const pinia = createPinia();
app.use(pinia);
// ⚡ CRITICAL FIX: Manually set active Pinia for SSR/early component access
// This ensures Pinia is available even before app.mount()
setActivePinia(pinia);
console.log('✅ Pinia installed and activated');
// ✅ NEW: Provide centralized project config FIRST
app.provide(PROJECT_CONFIG_KEY, eckpunktConfig);
console.log('🎯 Centralized project config provided:', eckpunktConfig.project.name);
// Provide API client and project config globally (BEFORE router)
app.provide('apiClient', apiClient);
app.provide('projectConfig', projectConfig);
app.provide('projectContext', projectContext); // Keep for backwards compatibility
app.provide('router', router); // ✅ FIX: Explicitly provide router for CartComponent and other shared components
console.log('🔌 API Client provided:', apiClient);
console.log('🏢 Project Context provided:', projectContext);
console.log('🗺️  Router provided explicitly'); // ✅ NEW
// Set project config on window for project-specific state management
window.projectConfig = restaurantConfig;
console.log('🔧 Global project config set:', window.projectConfig);
// SEO: Basic meta tags are set via restaurant.config.ts
// Analytics can be added here if needed
// ⚡ CRITICAL FIX: Router BEFORE mount
// Install router first, but don't trigger navigation yet
app.use(router);
console.log('✅ Router installed');
// Mount app - this makes Pinia available to all component contexts
app.mount('#app');
console.log('✅ App mounted');
void loadSiteConfig(projectConfig.publicApiBaseUrl).catch(() => {
    // Legacy compile-time config remains active when site-config is unavailable.
});
// Wait for router to be ready and log
router.isReady().then(() => {
    console.log('🎯 Project:', restaurantConfig.restaurant.name);
    console.log('🌐 API Base:', restaurantConfig.app.api.baseUrl);
    console.log('🍴 Multi-Project Template System Active!');
});
router.isReady().then(() => {
    // Dispatch custom event für Prerenderer
    document.dispatchEvent(new Event('app-rendered'));
    console.log('📄 App rendered - Prerenderer notified');
});
// ✅ PERFORMANCE: Initialize performance monitoring
if (process.env.NODE_ENV === 'development') {
    import('@shared/utils/performance').then(({ performanceMonitor }) => {
        performanceMonitor.markRenderComplete();
        performanceMonitor.checkWebVitals();
    });
}
console.log('🎯 Project:', restaurantConfig.restaurant.name);
console.log('🌐 API Base:', restaurantConfig.app.api.baseUrl);
console.log('🍴 Multi-Project Template System Active!');
